import ReplyAllOutlinedIcon from '@mui/icons-material/ReplyAllOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import { Stack } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React, { MouseEventHandler } from 'react';

import { DeleteIconButton } from '@/components/DeleteIconButton.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Props = (
  | {
      onForward: MouseEventHandler<HTMLButtonElement>;
      onReply: MouseEventHandler<HTMLButtonElement>;
      onReplyAll: MouseEventHandler<HTMLButtonElement>;
    }
  | {
      onForward?: MouseEventHandler<HTMLButtonElement>;
      onReply?: MouseEventHandler<HTMLButtonElement>;
      onReplyAll?: MouseEventHandler<HTMLButtonElement>;
    }
) & {
  emailOwnerId?: string;
  isDraft: boolean;
  onDelete?: MouseEventHandler<HTMLButtonElement>;
};

const IconSx = {
  '&:hover': {
    color: themeColors.blueDark,
  },
  color: themeColors.blue,
};

export const EmailControlButtons: React.FC<Props> = props => {
  const { emailOwnerId, isDraft, onDelete, onForward, onReply, onReplyAll } = props;
  const { externalUserId } = useUserContext();
  const { t } = useTranslationWithPrefix('');

  if (emailOwnerId && emailOwnerId !== externalUserId) {
    return <Typography variant="f12Regular">{t('views.emails.messages.notYourEmail')}</Typography>;
  }

  if (!onDelete && !onForward && !onReply && !onReplyAll) {
    return null;
  }

  const handleDeleteClick: MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    onDelete?.(event);
  };

  const handleForwardClick: MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    onForward?.(event);
  };

  const handleReplyClick: MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    onReply?.(event);
  };

  const handleReplyAllClick: MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    onReplyAll?.(event);
  };
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
      {!isDraft && onReply && (
        <IconButton onClick={handleReplyClick} sx={IconSx}>
          <ReplyOutlinedIcon />
        </IconButton>
      )}
      {!isDraft && onReplyAll && (
        <IconButton
          onClick={handleReplyAllClick}
          sx={{ '&:hover': { color: themeColors.blueDark }, color: themeColors.blue }}
        >
          <ReplyAllOutlinedIcon />
        </IconButton>
      )}
      {!isDraft && onForward && (
        <IconButton
          onClick={handleForwardClick}
          sx={{
            '&:hover': { color: themeColors.purple },
            color: themeColors.purpleLight,
            transform: 'scale(-1, 1)',
          }}
        >
          <ReplyOutlinedIcon />
        </IconButton>
      )}
      {onDelete && <DeleteIconButton onClick={handleDeleteClick} />}
    </Stack>
  );
};
